import gql from 'graphql-tag';

const PRINT_TEMPLATE_FRAGMENT = gql`
  fragment PrintTemplateFields on PrintTemplate {
    id
    organisationId
    name
    description
    status
    version
    hasUnpublishedChanges
    draftSpec
    publishedSpec
    publishedAt
    assets {
      id
      kind
      originalName
      contentType
      size
      url
      createdAt
    }
    basedOnBuiltinId
    createdAt
    updatedAt
  }
`;

const TEMPLATE_LIBRARY_CONFIG_FRAGMENT = gql`
  fragment TemplateLibraryConfigFields on TemplateLibraryConfig {
    hiddenBuiltinIds
    defaultTemplate {
      source
      id
    }
    brand {
      address
      phone
      email
      website
      vat
    }
    logo {
      originalName
      contentType
      size
      url
      updatedAt
    }
    fonts {
      id
      family
      weight
      italic
      originalName
      size
      url
      createdAt
    }
  }
`;

const TEMPLATE_LIBRARY_FRAGMENT = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  ${TEMPLATE_LIBRARY_CONFIG_FRAGMENT}
  fragment TemplateLibraryFields on TemplateLibrary {
    organisationId
    canEdit
    config {
      ...TemplateLibraryConfigFields
    }
    templates {
      ...PrintTemplateFields
    }
  }
`;

const PRINT_TEMPLATE_VERSION_FRAGMENT = gql`
  fragment PrintTemplateVersionFields on PrintTemplateVersion {
    id
    version
    spec
    name
    publishedAt
    publishedByName
    isLatest
    sheetCount
  }
`;

export const ORGANISATION_TEMPLATE_LIBRARY = gql`
  ${TEMPLATE_LIBRARY_FRAGMENT}
  query organisationTemplateLibrary($organisationId: ID!, $includeArchived: Boolean) {
    organisationTemplateLibrary(organisationId: $organisationId, includeArchived: $includeArchived) {
      ...TemplateLibraryFields
    }
  }
`;

export const PROJECT_TEMPLATE_LIBRARY = gql`
  ${TEMPLATE_LIBRARY_FRAGMENT}
  query projectTemplateLibrary($projectId: ID!) {
    projectTemplateLibrary(projectId: $projectId) {
      ...TemplateLibraryFields
    }
  }
`;

export const PROJECT_PRINT_TEMPLATE_VERSION = gql`
  ${PRINT_TEMPLATE_VERSION_FRAGMENT}
  query projectPrintTemplateVersion($projectId: ID!, $templateId: ID!, $version: Int!) {
    projectPrintTemplateVersion(projectId: $projectId, templateId: $templateId, version: $version) {
      ...PrintTemplateVersionFields
    }
  }
`;

export const PRINT_TEMPLATE = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  query printTemplate($organisationId: ID!, $templateId: ID!) {
    printTemplate(organisationId: $organisationId, templateId: $templateId) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_VERSIONS = gql`
  ${PRINT_TEMPLATE_VERSION_FRAGMENT}
  query printTemplateVersions($organisationId: ID!, $templateId: ID!) {
    printTemplateVersions(organisationId: $organisationId, templateId: $templateId) {
      ...PrintTemplateVersionFields
    }
  }
`;

export const ORGANISATION_TEMPLATE_LIBRARY_UPLOAD_SAS = gql`
  query organisationTemplateLibraryUploadSAS($organisationId: ID!) {
    organisationTemplateLibraryUploadSAS(organisationId: $organisationId)
  }
`;

export const PRINT_TEMPLATE_CREATE = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplateCreate($organisationId: ID!, $name: String!, $spec: JSON!, $basedOnBuiltinId: String, $duplicateOfTemplateId: ID) {
    printTemplateCreate(organisationId: $organisationId, name: $name, spec: $spec, basedOnBuiltinId: $basedOnBuiltinId, duplicateOfTemplateId: $duplicateOfTemplateId) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_UPDATE_DETAILS = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplateUpdateDetails($organisationId: ID!, $templateId: ID!, $name: String!, $description: String) {
    printTemplateUpdateDetails(organisationId: $organisationId, templateId: $templateId, name: $name, description: $description) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_SAVE_DRAFT = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplateSaveDraft($organisationId: ID!, $templateId: ID!, $spec: JSON!) {
    printTemplateSaveDraft(organisationId: $organisationId, templateId: $templateId, spec: $spec) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_PUBLISH = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplatePublish($organisationId: ID!, $templateId: ID!) {
    printTemplatePublish(organisationId: $organisationId, templateId: $templateId) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_DISCARD_DRAFT = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplateDiscardDraft($organisationId: ID!, $templateId: ID!) {
    printTemplateDiscardDraft(organisationId: $organisationId, templateId: $templateId) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_RESTORE_VERSION = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplateRestoreVersion($organisationId: ID!, $templateId: ID!, $version: Int!) {
    printTemplateRestoreVersion(organisationId: $organisationId, templateId: $templateId, version: $version) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_DELETE_VERSION = gql`
  mutation printTemplateDeleteVersion($organisationId: ID!, $templateId: ID!, $version: Int!) {
    printTemplateDeleteVersion(organisationId: $organisationId, templateId: $templateId, version: $version)
  }
`;

export const PRINT_TEMPLATE_SET_ARCHIVED = gql`
  ${PRINT_TEMPLATE_FRAGMENT}
  mutation printTemplateSetArchived($organisationId: ID!, $templateId: ID!, $archived: Boolean!) {
    printTemplateSetArchived(organisationId: $organisationId, templateId: $templateId, archived: $archived) {
      ...PrintTemplateFields
    }
  }
`;

export const PRINT_TEMPLATE_DELETE = gql`
  mutation printTemplateDelete($organisationId: ID!, $templateId: ID!) {
    printTemplateDelete(organisationId: $organisationId, templateId: $templateId)
  }
`;

export const PRINT_TEMPLATE_ATTACH_ASSET = gql`
  mutation printTemplateAttachAsset($organisationId: ID!, $templateId: ID!, $kind: PrintTemplateAssetKind!, $blobName: String!, $originalName: String!) {
    printTemplateAttachAsset(organisationId: $organisationId, templateId: $templateId, kind: $kind, blobName: $blobName, originalName: $originalName) {
      id
      kind
      originalName
      contentType
      size
      url
      createdAt
    }
  }
`;

export const ORGANISATION_TEMPLATE_LIBRARY_UPDATE_CONFIG = gql`
  ${TEMPLATE_LIBRARY_CONFIG_FRAGMENT}
  mutation organisationTemplateLibraryUpdateConfig($organisationId: ID!, $config: TemplateLibraryConfigInput!) {
    organisationTemplateLibraryUpdateConfig(organisationId: $organisationId, config: $config) {
      ...TemplateLibraryConfigFields
    }
  }
`;

export const ORGANISATION_TEMPLATE_LIBRARY_SET_LOGO = gql`
  ${TEMPLATE_LIBRARY_CONFIG_FRAGMENT}
  mutation organisationTemplateLibrarySetLogo($organisationId: ID!, $blobName: String, $originalName: String) {
    organisationTemplateLibrarySetLogo(organisationId: $organisationId, blobName: $blobName, originalName: $originalName) {
      ...TemplateLibraryConfigFields
    }
  }
`;

export const ORGANISATION_PRINT_FONT_ATTACH = gql`
  ${TEMPLATE_LIBRARY_CONFIG_FRAGMENT}
  mutation organisationPrintFontAttach($organisationId: ID!, $blobName: String!, $originalName: String!, $family: String!, $weight: PrintFontWeight!, $italic: Boolean!) {
    organisationPrintFontAttach(organisationId: $organisationId, blobName: $blobName, originalName: $originalName, family: $family, weight: $weight, italic: $italic) {
      ...TemplateLibraryConfigFields
    }
  }
`;

export const ORGANISATION_PRINT_FONT_REMOVE = gql`
  ${TEMPLATE_LIBRARY_CONFIG_FRAGMENT}
  mutation organisationPrintFontRemove($organisationId: ID!, $fontId: ID!) {
    organisationPrintFontRemove(organisationId: $organisationId, fontId: $fontId) {
      ...TemplateLibraryConfigFields
    }
  }
`;

/** The AI designs (or changes the given spec into) a template; nothing is stored. */
export const PRINT_TEMPLATE_GENERATE = gql`
  mutation printTemplateGenerate($organisationId: ID!, $prompt: String!, $spec: JSON, $image: String, $progressChannel: String) {
    printTemplateGenerate(organisationId: $organisationId, prompt: $prompt, spec: $spec, image: $image, progressChannel: $progressChannel) {
      spec
      summary
      name
    }
  }
`;
